// Die Eintraege der Befehlspalette als reine Logik (ST-UX-001, Bauvorgabe
// Slice 3): welche Gruppen und Optionen zu welchem Stand von Begriff und Abruf
// gehoeren. Keine DOM-Abhaengigkeit, damit die Reihenfolge und die Sperren
// ohne Browser zu pruefen sind.

import { contactLineOf, kindLabel, MINIMUM_SEARCH_TERM_LENGTH, type PartySummary } from '../parties.ts';

export type AktionId = 'anlegen' | 'kontakte' | 'wiedervorlagen' | 'kuerzel' | 'notiz';

export type Befehl =
  | { readonly art: 'kontakt'; readonly partyId: string }
  | { readonly art: 'aktion'; readonly aktion: AktionId }
  | { readonly art: 'alle-treffer'; readonly begriff: string }
  | { readonly art: 'erneut' };

export type GruppeId = 'zuletzt' | 'kontakte' | 'aktionen' | 'weitere';

export interface Eintrag {
  /** Stabil und eindeutig ueber alle Gruppen (ein Kontakt kann in zwei Gruppen stehen). */
  readonly id: string;
  readonly titel: string;
  readonly zweitzeile?: string;
  /** Nur bei Kontakten: Person oder Organisation fuer das Symbol. */
  readonly organisation?: boolean;
  /** Nur bei Kontakten: die Art als Text, fuer den Namen der Option. */
  readonly artLabel?: string;
  readonly taste?: string;
  readonly befehl: Befehl;
}

export interface Gruppe {
  readonly id: GruppeId;
  /** Ohne Titel steht die Gruppe ohne Kopf (die Schlusszeile "Alle Treffer"). */
  readonly label?: string;
  readonly eintraege: readonly Eintrag[];
}

export type Abruf = 'ruht' | 'laedt' | 'geladen' | 'gestoert';

export interface Eingabe {
  readonly begriff: string;
  readonly zuletzt: readonly PartySummary[];
  readonly treffer: readonly PartySummary[];
  readonly abruf: Abruf;
  readonly darfAnlegen: boolean;
  /** Die Rolle sieht die Kontakte: nur dann sucht die Palette, und nur dann fuehrt "Zu den Kontakten" irgendwohin. */
  readonly darfKontakte: boolean;
  readonly darfWiedervorlagen: boolean;
  /** Das Label des angemeldeten Kontextbefehls "notiz", sonst nichts. */
  readonly notizLabel: string | undefined;
}

export interface Eintraege {
  readonly gruppen: readonly Gruppe[];
  /** Alle Optionen in Bedienreihenfolge (Pfeiltasten laufen darueber). */
  readonly optionen: readonly Eintrag[];
  readonly suchtModus: boolean;
  readonly keinTreffer: boolean;
}

/**
 * Was die Palette aus dem Begriff abruft. EIN Eingang "Begriff -> Abrufart":
 * eine spaetere Fachabfragesprache (ST-SEA-002, CM-SEA-004..016) ergibt hier
 * eine weitere Abrufart, ohne dass Palette oder Eintraege davon wissen muessen.
 * Heute gibt es genau eine: Name oder Kennung ab der Mindestlaenge.
 */
export type Abrufart = { readonly art: 'keine' } | { readonly art: 'name-oder-kennung'; readonly term: string };

export function abrufartFuer(begriff: string, darfKontakte: boolean): Abrufart {
  const term = begriff.trim();
  return darfKontakte && term.length >= MINIMUM_SEARCH_TERM_LENGTH ? { art: 'name-oder-kennung', term } : { art: 'keine' };
}

export const HOECHSTENS_ZULETZT = 5;
export const HOECHSTENS_TREFFER = 8;

function kontaktEintrag(vorsatz: string, party: PartySummary): Eintrag {
  const zweitzeile = contactLineOf(party) ?? kindLabel(party.kind);
  return {
    id: `${vorsatz}:${party.partyId}`,
    titel: party.displayName,
    zweitzeile,
    organisation: party.kind === 'organization',
    artLabel: kindLabel(party.kind),
    befehl: { art: 'kontakt', partyId: party.partyId },
  };
}

function aktionen(eingabe: Eingabe): readonly Eintrag[] {
  const liste: Eintrag[] = [];
  if (eingabe.darfAnlegen) {
    liste.push({ id: 'aktion:anlegen', titel: 'Kontakt anlegen', taste: 'N', befehl: { art: 'aktion', aktion: 'anlegen' } });
  }
  if (eingabe.darfKontakte) {
    liste.push({ id: 'aktion:kontakte', titel: 'Zu den Kontakten', taste: 'G K', befehl: { art: 'aktion', aktion: 'kontakte' } });
  }
  if (eingabe.darfWiedervorlagen) {
    liste.push({ id: 'aktion:wiedervorlagen', titel: 'Zu den Wiedervorlagen', taste: 'G W', befehl: { art: 'aktion', aktion: 'wiedervorlagen' } });
  }
  liste.push({ id: 'aktion:kuerzel', titel: 'Tastenkürzel anzeigen', taste: '?', befehl: { art: 'aktion', aktion: 'kuerzel' } });
  if (eingabe.notizLabel !== undefined) {
    liste.push({ id: 'aktion:notiz', titel: eingabe.notizLabel, taste: 'C', befehl: { art: 'aktion', aktion: 'notiz' } });
  }
  return liste;
}

function enthaelt(text: string, begriff: string): boolean {
  return text.toLocaleLowerCase('de').includes(begriff.toLocaleLowerCase('de'));
}

export function eintraegeFuer(eingabe: Eingabe): Eintraege {
  const begriff = eingabe.begriff.trim();
  const suchtModus = abrufartFuer(begriff, eingabe.darfKontakte).art !== 'keine';
  const gruppen: Gruppe[] = [];
  let keinTreffer = false;

  if (!suchtModus) {
    if (eingabe.zuletzt.length > 0) {
      gruppen.push({
        id: 'zuletzt',
        label: 'Zuletzt von mir bearbeitet',
        eintraege: eingabe.zuletzt.slice(0, HOECHSTENS_ZULETZT).map((party) => kontaktEintrag('zuletzt', party)),
      });
    }
    gruppen.push({ id: 'aktionen', label: 'Aktionen', eintraege: aktionen(eingabe) });
  } else {
    if (eingabe.abruf === 'gestoert') {
      gruppen.push({
        id: 'weitere',
        eintraege: [{ id: 'erneut', titel: 'Erneut versuchen', befehl: { art: 'erneut' } }],
      });
    }
    // Beim Nachladen bleiben die vorigen Treffer stehen: sonst rutschten die
    // Aktionen bei jedem Tastendruck nach oben (M-03, Kontakte vor Befehlen).
    if (eingabe.abruf === 'laedt' && eingabe.treffer.length > 0) {
      gruppen.push({
        id: 'kontakte',
        label: 'Kontakte',
        eintraege: eingabe.treffer.slice(0, HOECHSTENS_TREFFER).map((party) => kontaktEintrag('kontakt', party)),
      });
    }
    if (eingabe.abruf === 'geladen') {
      if (eingabe.treffer.length > 0) {
        gruppen.push({
          id: 'kontakte',
          label: 'Kontakte',
          eintraege: eingabe.treffer.slice(0, HOECHSTENS_TREFFER).map((party) => kontaktEintrag('kontakt', party)),
        });
      } else {
        keinTreffer = true;
        if (eingabe.darfAnlegen) {
          gruppen.push({
            id: 'weitere',
            eintraege: [{ id: 'anlegen-leer', titel: 'Kontakt anlegen', befehl: { art: 'aktion', aktion: 'anlegen' } }],
          });
        }
      }
    }
    const passend = aktionen(eingabe).filter((eintrag) => enthaelt(eintrag.titel, begriff));
    if (passend.length > 0) {
      gruppen.push({ id: 'aktionen', label: 'Aktionen', eintraege: passend });
    }
    gruppen.push({
      id: 'weitere',
      eintraege: [
        {
          id: 'alle-treffer',
          titel: `Alle Treffer für „${begriff}“ in der Kontaktliste`,
          zweitzeile: 'ohne Filter',
          befehl: { art: 'alle-treffer', begriff },
        },
      ],
    });
  }

  return { gruppen, optionen: gruppen.flatMap((gruppe) => gruppe.eintraege), suchtModus, keinTreffer };
}
